import { useEffect, useState, useCallback } from 'react';
import {
  monitoring,
  type MonitoringOverview,
  type MonitoringSession,
  type MonitoringError,
  type MonitoringRequest,
  type MonitoringDatabase,
  type MonitoringSystem,
} from '../../api';

type Tab = 'overview' | 'sessions' | 'errors' | 'requests' | 'system' | 'database';
type Window = '1m' | '5m' | '1h' | '24h';

const WINDOWS: Window[] = ['1m', '5m', '1h', '24h'];

export function PlatformMonitoringPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [window, setWindow] = useState<Window>('1h');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [overview, setOverview] = useState<MonitoringOverview | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const refreshOverview = useCallback(async () => {
    try {
      const r = await monitoring.overview(window);
      setOverview(r);
      setErr(null);
    } catch {
      setErr('Failed to load monitoring data.');
    }
  }, [window]);

  useEffect(() => {
    refreshOverview();
    if (!autoRefresh) return;
    const id = setInterval(refreshOverview, 10_000);
    return () => clearInterval(id);
  }, [refreshOverview, autoRefresh]);

  return (
    <div>
      <div className="toolbar" style={{ flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0 }}>Monitoring</h1>
          <div style={{ color: 'var(--fg-3)', fontSize: '0.88em', marginTop: '0.2em' }}>
            Live application health, active sessions and error log
          </div>
        </div>
        <div className="spacer" />
        <div style={{ display: 'flex', gap: '0.6em', alignItems: 'center' }}>
          <label style={{ fontSize: '0.85em', color: 'var(--fg-3)' }}>Window</label>
          <select value={window} onChange={(e) => setWindow(e.target.value as Window)}
            style={{ width: 'auto' }}>
            {WINDOWS.map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.3em', fontSize: '0.85em' }}>
            <input type="checkbox" checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ width: 'auto' }} />
            Auto-refresh 10s
          </label>
          <button onClick={refreshOverview}>Refresh</button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      {/* KPI strip */}
      {overview && <KpiStrip kpis={overview.kpis} window={window} />}

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: '0.3em', marginTop: '1.4em', marginBottom: '1em',
        borderBottom: '1px solid var(--border)',
      }}>
        <TabBtn active={tab === 'overview'}  onClick={() => setTab('overview')}>Overview</TabBtn>
        <TabBtn active={tab === 'sessions'}  onClick={() => setTab('sessions')}>Sessions ({overview?.kpis.active_sessions ?? 0})</TabBtn>
        <TabBtn active={tab === 'errors'}    onClick={() => setTab('errors')}>Errors ({overview?.kpis.errors_logged ?? 0})</TabBtn>
        <TabBtn active={tab === 'requests'}  onClick={() => setTab('requests')}>Requests</TabBtn>
        <TabBtn active={tab === 'system'}    onClick={() => setTab('system')}>System</TabBtn>
        <TabBtn active={tab === 'database'}  onClick={() => setTab('database')}>Database</TabBtn>
      </div>

      {tab === 'overview' && overview && <OverviewTab overview={overview} />}
      {tab === 'sessions' && <SessionsTab autoRefresh={autoRefresh} />}
      {tab === 'errors' && <ErrorsTab autoRefresh={autoRefresh} />}
      {tab === 'requests' && <RequestsTab window={window} autoRefresh={autoRefresh} />}
      {tab === 'system' && <SystemTab autoRefresh={autoRefresh} />}
      {tab === 'database' && <DatabaseTab autoRefresh={autoRefresh} />}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      style={{
        background: 'transparent', border: 'none', borderRadius: 0,
        borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
        color: active ? 'var(--fg-1)' : 'var(--fg-3)',
        padding: '0.6em 1em', marginBottom: '-1px', cursor: 'pointer',
        fontWeight: active ? 600 : 400,
      }}>{children}</button>
  );
}

/* ============================================================ KPI strip */

function KpiStrip({ kpis, window }: { kpis: MonitoringOverview['kpis']; window: string }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
      gap: '0.7em',
      marginTop: '1em',
    }}>
      <Kpi label="Uptime" value={formatUptime(kpis.uptime_s)} />
      <Kpi label="Active sessions" value={String(kpis.active_sessions)} />
      <Kpi label={`Req/s (${window})`} value={kpis.req_per_s.toFixed(2)} />
      <Kpi label="Avg response" value={`${kpis.avg_response_ms}ms`}
        color={kpis.avg_response_ms < 300 ? 'var(--accent-2)' : 'var(--warn)'} />
      <Kpi label={`Error rate`} value={`${kpis.error_rate_pct}%`} sub={`last ${window}`}
        color={kpis.error_rate_pct === 0 ? 'var(--accent-2)' : 'var(--danger)'} />
      <Kpi label="Errors logged" value={String(kpis.errors_logged)} />
      <Kpi label="CPU load (1m)" value={kpis.cpu_load_1m.toFixed(2)} sub={`${(kpis.cpu_load_1m * 100 / 4).toFixed(0)}%`} />
      <Kpi label="Memory"
        value={`${Math.round((kpis.memory_used_mb / kpis.memory_total_mb) * 100)}%`}
        sub={`${kpis.memory_free_mb}MB free`} />
      <Kpi label="Heap"
        value={`${kpis.heap_used_mb}MB`}
        sub={`${Math.round((kpis.heap_used_mb / kpis.heap_total_mb) * 100)}% of ${kpis.heap_total_mb}MB`} />
      <Kpi label="Node.js" value={kpis.node_version} sub={kpis.platform} small />
      {kpis.disk_used_pct !== null && (
        <Kpi label="Disk" value={`${kpis.disk_used_pct}%`} sub={`${kpis.disk_free_gb}GB free`} />
      )}
      <Kpi label="Restarts" value={String(kpis.restart_count)} sub="since deploy" />
      <Kpi label="PG conns" value={String(kpis.pg_connections)} sub="active" />
      <Kpi label="Logins failed (24h)" value={String(kpis.logins_failed_24h)}
        color={kpis.logins_failed_24h === 0 ? undefined : 'var(--warn)'} />
    </div>
  );
}

function Kpi({ label, value, sub, color, small }: {
  label: string; value: string; sub?: string; color?: string; small?: boolean;
}) {
  return (
    <div style={{
      background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 6,
      padding: '0.8em 1em',
    }}>
      <div style={{ fontSize: '0.72em', color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
      <div style={{ fontSize: small ? '1em' : '1.5em', fontWeight: 600, marginTop: '0.25em', color: color || 'var(--fg-1)' }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: '0.75em', color: 'var(--fg-3)', marginTop: '0.15em' }}>{sub}</div>}
    </div>
  );
}

/* ============================================================ Overview tab */

function OverviewTab({ overview }: { overview: MonitoringOverview }) {
  const { http } = overview;
  const total = http.total;
  const bars: Array<{ label: string; key: keyof typeof http.per_status_class; color: string }> = [
    { label: '2xx Success', key: '2xx', color: 'var(--accent-2)' },
    { label: '3xx Redirect', key: '3xx', color: 'var(--fg-3)' },
    { label: '4xx Client', key: '4xx', color: 'var(--warn)' },
    { label: '5xx Server', key: '5xx', color: 'var(--danger)' },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em' }}>
      <div className="card" style={{ margin: 0 }}>
        <h3 style={{ marginTop: 0 }}>HTTP responses ({overview.window})</h3>
        {bars.map((b) => {
          const count = http.per_status_class[b.key];
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={b.key} style={{ marginBottom: '0.7em' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2em' }}>
                <span style={{ color: b.color }}>{b.label}</span>
                <span><strong>{count}</strong> <span style={{ color: 'var(--fg-3)' }}>({pct}%)</span></span>
              </div>
              <div style={{ height: 8, background: 'var(--bg-2)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: b.color }} />
              </div>
            </div>
          );
        })}
        <div style={{ marginTop: '1em', color: 'var(--fg-3)', fontSize: '0.85em' }}>
          Total: {total.toLocaleString()} requests
        </div>
      </div>
      <div className="card" style={{ margin: 0 }}>
        <h3 style={{ marginTop: 0 }}>Top endpoints ({overview.window})</h3>
        <table style={{ marginTop: 0 }}>
          <thead><tr><th>Path</th><th style={{ textAlign: 'right' }}>Hits</th><th style={{ textAlign: 'right' }}>Avg</th></tr></thead>
          <tbody>
            {http.top_endpoints.length === 0 && (
              <tr><td colSpan={3} style={{ color: 'var(--fg-3)' }}>No traffic in window.</td></tr>
            )}
            {http.top_endpoints.map((e) => (
              <tr key={e.path}>
                <td style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{e.path}</td>
                <td style={{ textAlign: 'right' }}>{e.hits}</td>
                <td style={{ textAlign: 'right', color: 'var(--fg-3)' }}>{e.avg_ms}ms</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================ Sessions tab */

function SessionsTab({ autoRefresh }: { autoRefresh: boolean }) {
  const [sessions, setSessions] = useState<MonitoringSession[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);

  async function refresh() {
    try { setSessions((await monitoring.sessions(includeInactive)).sessions); } catch { /* */ }
  }
  useEffect(() => {
    refresh();
    if (!autoRefresh) return;
    const id = setInterval(refresh, 10_000);
    return () => clearInterval(id);
  }, [autoRefresh, includeInactive]);

  async function terminate(id: string) {
    if (!confirm('Force-end this session? The user will be logged out on next activity.')) return;
    await monitoring.terminateSession(id);
    refresh();
  }

  return (
    <div>
      <div style={{ marginBottom: '0.8em' }}>
        <label style={{ fontSize: '0.85em', color: 'var(--fg-3)' }}>
          <input type="checkbox" style={{ width: 'auto', marginRight: '0.4em' }}
            checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
          Include ended sessions
        </label>
      </div>
      {sessions.length === 0 ? (
        <div className="empty">No active sessions.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>User</th><th>IP</th><th>Started</th><th>Last activity</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.id}>
                <td>
                  <strong>{s.email}</strong>
                  {s.is_platform_admin && <span style={{ color: 'var(--accent)', marginLeft: '0.4em', fontSize: '0.78em' }}>· P_ADMIN</span>}
                  <div style={{ fontSize: '0.75em', color: 'var(--fg-3)' }}>
                    {(s.user_agent || '').slice(0, 60)}
                  </div>
                </td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{s.ip || '—'}</td>
                <td>{new Date(s.started_at).toLocaleString()}</td>
                <td>{formatRelative(s.last_activity_at)}</td>
                <td>
                  {s.active
                    ? <span className="badge active">active</span>
                    : <span className="badge offline">ended</span>}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {s.active && <button className="danger" onClick={() => terminate(s.id)}>End</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ============================================================ Errors tab */

function ErrorsTab({ autoRefresh }: { autoRefresh: boolean }) {
  const [errors, setErrors] = useState<MonitoringError[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);

  async function refresh() {
    try { setErrors((await monitoring.errors()).errors); } catch { /* */ }
  }
  useEffect(() => {
    refresh();
    if (!autoRefresh) return;
    const id = setInterval(refresh, 10_000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  async function clearAll() {
    if (!confirm('Clear all logged errors?')) return;
    await monitoring.clearErrors();
    refresh();
  }

  return (
    <div>
      <div style={{ marginBottom: '0.8em', display: 'flex', justifyContent: 'flex-end' }}>
        <button className="danger" onClick={clearAll} disabled={errors.length === 0}>Clear all</button>
      </div>
      {errors.length === 0 ? (
        <div className="empty">No errors logged. ✓</div>
      ) : (
        <table>
          <thead>
            <tr><th>Time</th><th>Method</th><th>Path</th><th>Status</th><th>User</th><th>Message</th></tr>
          </thead>
          <tbody>
            {errors.map((e) => (
              <>
                <tr key={e.id} onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                  style={{ cursor: 'pointer' }}>
                  <td>{new Date(e.occurred_at).toLocaleString()}</td>
                  <td><code>{e.method || '—'}</code></td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{e.path || '—'}</td>
                  <td><span className="badge" style={{ background: 'rgba(220,38,38,0.15)', color: 'var(--danger)' }}>{e.status || '—'}</span></td>
                  <td>{e.user_email || '—'}</td>
                  <td style={{ color: 'var(--danger)' }}>{e.message}</td>
                </tr>
                {expanded === e.id && e.stack && (
                  <tr key={`${e.id}-stack`}>
                    <td colSpan={6} style={{ background: 'var(--bg-0)' }}>
                      <pre style={{
                        margin: 0, padding: '0.8em', fontSize: '0.75em',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        color: 'var(--fg-3)',
                      }}>{e.stack}</pre>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ============================================================ Requests tab */

function RequestsTab({ window, autoRefresh }: { window: string; autoRefresh: boolean }) {
  const [requests, setRequests] = useState<MonitoringRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('');

  async function refresh() {
    try {
      const r = await monitoring.requests(window);
      setRequests(r.requests);
      setTotal(r.total_in_window);
    } catch { /* */ }
  }
  useEffect(() => {
    refresh();
    if (!autoRefresh) return;
    const id = setInterval(refresh, 10_000);
    return () => clearInterval(id);
  }, [window, autoRefresh]);

  const f = filter.trim().toLowerCase();
  const visible = f
    ? requests.filter((r) =>
        r.path.toLowerCase().includes(f) || r.method.toLowerCase().includes(f) || String(r.status).includes(f))
    : requests;

  return (
    <div>
      <div style={{ marginBottom: '0.8em', display: 'flex', gap: '0.6em', alignItems: 'center' }}>
        <input placeholder="Filter by path, method, status..."
          value={filter} onChange={(e) => setFilter(e.target.value)}
          style={{ flex: 1 }} />
        <span style={{ color: 'var(--fg-3)', fontSize: '0.85em' }}>
          {visible.length} shown · {total} total in {window}
        </span>
      </div>
      {visible.length === 0 ? (
        <div className="empty">No requests in window.</div>
      ) : (
        <table>
          <thead><tr><th>Time</th><th>Method</th><th>Path</th><th>Status</th><th style={{ textAlign: 'right' }}>Duration</th><th>IP</th></tr></thead>
          <tbody>
            {visible.map((r, i) => (
              <tr key={i}>
                <td style={{ whiteSpace: 'nowrap' }}>{new Date(r.ts).toLocaleTimeString()}</td>
                <td><code>{r.method}</code></td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{r.path}</td>
                <td><StatusBadge status={r.status} /></td>
                <td style={{ textAlign: 'right', color: r.duration_ms > 500 ? 'var(--warn)' : 'var(--fg-3)' }}>
                  {r.duration_ms}ms
                </td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.78em', color: 'var(--fg-3)' }}>{r.ip || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: number }) {
  const cls = status >= 500 ? 'var(--danger)' : status >= 400 ? 'var(--warn)' : status >= 300 ? 'var(--fg-3)' : 'var(--accent-2)';
  return <span style={{ color: cls, fontWeight: 600 }}>{status}</span>;
}

/* ============================================================ System tab */

function SystemTab({ autoRefresh }: { autoRefresh: boolean }) {
  const [sys, setSys] = useState<MonitoringSystem['system'] | null>(null);

  async function refresh() {
    try { setSys((await monitoring.system()).system); } catch { /* */ }
  }
  useEffect(() => {
    refresh();
    if (!autoRefresh) return;
    const id = setInterval(refresh, 10_000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  if (!sys) return <div>Loading…</div>;

  const rows: Array<[string, string]> = [
    ['Uptime', formatUptime(sys.uptime_s)],
    ['Node.js', sys.node_version],
    ['Platform', sys.platform],
    ['CPUs', String(sys.cpu_count)],
    ['CPU load (1m)', sys.cpu_load_1m.toFixed(3)],
    ['Memory used', `${sys.memory_used_mb} MB / ${sys.memory_total_mb} MB`],
    ['Memory free', `${sys.memory_free_mb} MB`],
    ['Heap used', `${sys.heap_used_mb} MB / ${sys.heap_total_mb} MB`],
    ['Disk used', sys.disk_used_pct !== null ? `${sys.disk_used_pct}%` : 'n/a'],
    ['Disk free', sys.disk_free_gb !== null ? `${sys.disk_free_gb} GB` : 'n/a'],
    ['Restart count', String(sys.restart_count)],
  ];

  return (
    <div className="card" style={{ margin: 0, maxWidth: 600 }}>
      <h3 style={{ marginTop: 0 }}>System</h3>
      <table>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td style={{ color: 'var(--fg-3)', width: '40%' }}>{k}</td>
              <td style={{ fontFamily: 'monospace' }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ============================================================ Database tab */

function DatabaseTab({ autoRefresh }: { autoRefresh: boolean }) {
  const [db, setDb] = useState<MonitoringDatabase | null>(null);

  async function refresh() {
    try { setDb(await monitoring.database()); } catch { /* */ }
  }
  useEffect(() => {
    refresh();
    if (!autoRefresh) return;
    const id = setInterval(refresh, 10_000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  if (!db) return <div>Loading…</div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em' }}>
      <div className="card" style={{ margin: 0 }}>
        <h3 style={{ marginTop: 0 }}>Connection pool</h3>
        <table>
          <tbody>
            <tr><td style={{ color: 'var(--fg-3)' }}>Pool total</td><td>{db.pool.total}</td></tr>
            <tr><td style={{ color: 'var(--fg-3)' }}>Pool idle</td><td>{db.pool.idle}</td></tr>
            <tr><td style={{ color: 'var(--fg-3)' }}>Pool waiting</td><td>{db.pool.waiting}</td></tr>
            <tr><td style={{ color: 'var(--fg-3)' }}>DB connections</td><td>{db.connections}</td></tr>
            <tr><td style={{ color: 'var(--fg-3)' }}>Active queries</td><td>{db.active_queries}</td></tr>
            <tr><td style={{ color: 'var(--fg-3)' }}>Database size</td><td>{formatBytes(db.db_size_bytes)}</td></tr>
          </tbody>
        </table>
      </div>
      <div className="card" style={{ margin: 0 }}>
        <h3 style={{ marginTop: 0 }}>Largest tables</h3>
        <table>
          <thead><tr><th>Table</th><th style={{ textAlign: 'right' }}>Size</th><th style={{ textAlign: 'right' }}>Rows ~</th></tr></thead>
          <tbody>
            {db.tables.map((t) => (
              <tr key={t.table}>
                <td style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>{t.table}</td>
                <td style={{ textAlign: 'right' }}>{formatBytes(t.size_bytes)}</td>
                <td style={{ textAlign: 'right', color: 'var(--fg-3)' }}>{t.row_estimate.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================ Helpers */

function formatUptime(s: number): string {
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(iso).toLocaleString();
}
